/**
 * What the QuickBooks import list offers, given the QuickBooks records the
 * server says look like something FreshOrder already has
 * (server/services/quickbooksImportMatching.ts).
 *
 *  - `exact` (same name as an unlinked FreshOrder record): not offered. The
 *    server would skip it anyway; it is linked under Suggested Matches instead.
 *  - `high` / `partial` (a looser match): offered, flagged and left out of
 *    "select all", so it is imported only on purpose.
 */
export interface LikelyExisting {
  qbId: string;
  existingId: number;
  existingName: string;
  matchConfidence: "exact" | "high" | "partial";
}

export interface ImportChoices<T> {
  /** The rows of the import list. */
  offered: T[];
  /** The rows "select all" selects: offered and matching nothing here. */
  selectAllIds: string[];
  /** For a flagged row, the FreshOrder record it may be a copy of. */
  possibleMatchFor: (qbId: string) => LikelyExisting | undefined;
  /** Offered rows that are flagged. */
  possibleMatchCount: number;
  /** Not offered: the same name is already in FreshOrder. */
  sameNameCount: number;
}

export function quickbooksImportChoices<T extends { id: string }>(
  qbRecords: T[],
  linkedQbIds: Array<string | null | undefined>,
  likelyExisting: LikelyExisting[] = [],
): ImportChoices<T> {
  const linked = new Set(linkedQbIds.filter(Boolean));
  const likely = new Map(likelyExisting.map((match) => [match.qbId, match]));
  const notLinked = qbRecords.filter((record) => !linked.has(record.id));
  const offered = notLinked.filter((record) => likely.get(record.id)?.matchConfidence !== "exact");
  const selectAllIds = offered.filter((record) => !likely.has(record.id)).map((record) => record.id);

  return {
    offered,
    selectAllIds,
    possibleMatchFor: (qbId) => likely.get(qbId),
    possibleMatchCount: offered.length - selectAllIds.length,
    sameNameCount: notLinked.length - offered.length,
  };
}
